import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { congesApi, type TypeAbsence } from '../../services/conges-api';
import { libellePlafond, lireMecanismes } from '../../lib/champs-type-absence';

/**
 * Le « ? » d'un type d'absence — ce qu'il faut savoir avant de demander.
 *
 * Composant RÉUTILISABLE, et c'est le point : il servira au formulaire de
 * demande (Lot 3) comme aux écrans de validation (Lot 4). Le manager et la RH
 * ont besoin du même barème que le salarié, au même endroit — à côté du nombre
 * de jours demandés.
 *
 * Il n'affiche que ce que le type porte : un barème s'il en a un, le rappel de
 * son plafond, la mention « non rémunéré ». Un type sans rien à dire n'affiche
 * pas de « ? » du tout — un point d'interrogation qui ouvre sur du vide apprend
 * à ne plus cliquer.
 *
 * Le barème est INFORMATIF : aucun contrôle n'en découle. Le contrôle du nombre
 * de jours est humain, porté par le circuit à deux niveaux (specs §B3 v1.9).
 */
/** Marge minimale entre la bulle et le bord de la fenêtre. */
const MARGE = 8;

export function AideType({ type }: { type: TypeAbsence }) {
  const [ouvert, setOuvert] = useState(false);
  const bouton = useRef<HTMLButtonElement>(null);
  const bulle = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; maxHeight: number }>({
    left: 0,
    top: 0,
    maxHeight: 400,
  });

  /**
   * Place la bulle DANS la fenêtre, toujours.
   *
   * Elle était positionnée en absolu sous le bouton : à droite d'un écran ou en
   * bas d'un formulaire, elle sortait du cadre et n'en laissait voir qu'un
   * angle. On la passe en `fixed` et on calcule ses coordonnées à partir du
   * bouton, bornées aux dimensions réelles de la fenêtre — c'est la seule façon
   * de garantir qu'elle reste lisible quel que soit l'endroit où le « ? » tombe.
   */
  const placer = useCallback(() => {
    const b = bouton.current?.getBoundingClientRect();
    if (!b) return;
    const largeur = bulle.current?.offsetWidth ?? 320;
    const hauteur = bulle.current?.offsetHeight ?? 240;

    const left = Math.max(
      MARGE,
      Math.min(b.left, window.innerWidth - largeur - MARGE),
    );

    const placeDessous = window.innerHeight - b.bottom - MARGE * 2;
    const placeDessus = b.top - MARGE * 2;
    // On ouvre vers le bas si la place y suffit, sinon vers le haut. À défaut,
    // on garde le côté le plus large et la bulle défile.
    const versLeBas = hauteur <= placeDessous || placeDessous >= placeDessus;
    const top = versLeBas ? b.bottom + MARGE : Math.max(MARGE, b.top - hauteur - MARGE);

    setPosition({
      left,
      top,
      maxHeight: Math.max(160, versLeBas ? placeDessous : placeDessus),
    });
  }, []);

  useLayoutEffect(() => {
    if (ouvert) placer();
  }, [ouvert, placer]);

  useEffect(() => {
    if (!ouvert) return;
    // Le contenu du barème arrive après coup : on replace une fois qu'il est là.
    const t = setTimeout(placer, 50);
    window.addEventListener('resize', placer);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', placer);
    };
  }, [ouvert, placer]);
  const mecanismes = lireMecanismes(type.mecanismes);
  const plafond = libellePlafond(type);
  const aBareme = mecanismes.includes('bareme_aide');

  const { data } = useQuery({
    queryKey: ['conges', 'baremes', type.id],
    queryFn: () => congesApi.baremes.liste(type.id),
    // On ne charge le barème qu'à l'ouverture : sur un formulaire qui liste
    // douze types, douze requêtes au chargement pour du texte que personne
    // n'ouvrira coûteraient plus que le service rendu.
    enabled: ouvert && aBareme,
  });

  const lignes = (data?.baremes ?? []).filter((b) => b.actif);
  const nonRemunere = mecanismes.includes('non_remunere');
  const aQuelqueChoseADire = aBareme || Boolean(plafond) || Boolean(type.aide_plafond) || nonRemunere;
  if (!aQuelqueChoseADire) return null;

  return (
    <span className="relative inline-block">
      <button
        ref={bouton}
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        aria-label={`En savoir plus sur « ${type.libelle} »`}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-nuit/25 text-xs font-bold text-nuit/70 hover:border-nuit/50 hover:text-nuit"
      >
        ?
      </button>

      {ouvert && (
        <>
          {/* Un clic n'importe où referme : sur mobile, viser à nouveau un
              bouton de 24 px pour fermer est une épreuve. */}
          <span className="fixed inset-0 z-10" onClick={() => setOuvert(false)} aria-hidden />
          <span
            ref={bulle}
            style={{
              left: position.left,
              top: position.top,
              maxHeight: position.maxHeight,
            }}
            className="fixed z-20 block w-80 max-w-[calc(100vw-16px)] overflow-y-auto rounded-lg border border-nuit/15 bg-white p-3 text-left shadow-lg"
          >
            <span className="mb-1 block font-titre text-sm font-bold text-nuit">{type.libelle}</span>

            {nonRemunere && (
              <span className="mb-2 block rounded border border-jaune-fond/60 border-l-4 border-l-jaune-fond px-2 py-1 text-xs text-jaune">
                Absence non rémunérée : ces jours ne sont pas payés.
              </span>
            )}

            {/* Le PLAFOND d'abord, en clair : « 3 jours » est ce que le salarié
                vient chercher. Le texte d'aide le nuance ensuite. */}
            {plafond && (
              <span className="mb-1 block text-sm font-medium text-nuit">{plafond}</span>
            )}

            {type.aide_plafond && (
              <span className="mb-2 block text-xs text-nuit/70">{type.aide_plafond}</span>
            )}

            {aBareme && lignes.length > 0 && (
              <>
                {/* La phrase vient de la fiche du type : elle cite une source
                    de droit, et une source de droit ne se code pas en dur. */}
                <span className="mb-1 block text-xs font-medium text-nuit">
                  {type.bareme_intro || 'Durées prévues par la convention collective Syntec'}
                </span>
                <span className="block space-y-1">
                  {lignes.map((b) => (
                    <span key={b.id} className="block text-xs text-nuit/80">
                      <strong className="text-nuit">
                        {b.nb_jours} jour{b.nb_jours > 1 ? 's' : ''}
                      </strong>{' '}
                      : {b.libelle}
                      {b.precisions && (
                        <span className="block pl-2 text-nuit/60">{b.precisions}</span>
                      )}
                    </span>
                  ))}
                </span>
                <span className="mt-2 block text-xs text-nuit/50">
                  Ces durées sont indicatives : votre demande est examinée par votre manager puis
                  par la RH.
                </span>
              </>
            )}
          </span>
        </>
      )}
    </span>
  );
}
